import { Attachment } from './Attachment'
import { MarketSegmentFilterParams } from './MarketSegment'

export enum Origin {
  HOST = 'host',
  MANUAL = 'manual',
  MEMBER = 'member',
}

export enum Priority {
  HIGH = 'High',
  LOW = 'Low',
  MEDIUM = 'Medium',
}

export enum Status {
  ALL = 'All',
  AWAITING_RESPONSE = 'Awaiting response',
  CLOSED = 'Closed',
  ESCALATED = 'Escalated',
  NEEDS_ACTION = 'Needs action',
  NEW = 'New',
  OPEN = 'Open',
  WORKING = 'Working',
}

export enum Type {
  BILLING = 'Billing',
  CONVERSATION = 'Conversation',
  HOME = 'Home',
  MAINTENANCE = 'Maintenance',
  RESERVATION = 'Reservation',
}

export interface Ticket {
  createdAt: string
  id: number
  internalTicketStatus: string
  origin: Origin
  priority: Priority
  subject: string
  ticketReason: string
  ticketType: Type
}

export interface User {
  fullName: string | null
  id: number
}

export interface DirectPartnerUser extends User {
  email: string
  phone: string | null
}

export interface TicketDetail {
  allowAsParent: boolean
  assignee?: User
  attachments: null | Array<Attachment>
  childTickets: Array<{
    assignee?: { fullName: string }
    createdAt: string
    id: number
    internalTicketStatus: string
    priority: Priority
    subject: string
    ticketType: string
  }>
  closedAt: string | null
  contact?: User
  createdAt: string
  creator?: User
  description: string
  externalId?: string | null
  externalSource?: string | null
  home: null | {
    id: number
    property: {
      directPartnerUser?: DirectPartnerUser
      isAutopilot?: boolean
      landingDirect?: boolean
    }
    propertyOnlinePortal: string | null
    propertyPortalPassword: string | null
    propertyPortalUsername: string | null
    searchDocument?: string
    uninstallDate?: string | null
  }
  id: number
  internalTicketStatus: string | null
  marketSegment: null | {
    id: number
    name: string | null
  }
  parentTicket: { id: number; subject: string; ticketType?: string } | null
  parentTicketId: number | null
  permissionToEnter?: boolean
  priority: Priority | null
  reservation: null | {
    id: number
  }
  subject: string | null
  /**
   * Emojis allowed for reactions on this ticket's comments.
   * Only present for tickets from platforms that support reactions (e.g., Airbnb).
   * When null/undefined, reaction add UI should be hidden.
   */
  supportedReactions?: string[] | null
  ticketHomeArea: string | null
  ticketHomeAreaId: number | null
  ticketReason: string | null
  ticketReasonId: number
  ticketStatusId: number
  ticketType?: string
  ticketTypeId: number
}

export interface TicketFilterParams extends MarketSegmentFilterParams {
  createdAt?: {
    end?: string | null
    start?: string | null
  }
  allowAsParent?: boolean
  includeInternalTickets?: boolean
  isRoot?: boolean
  parentTicketId?: number | null
  priority?: string | null
  search?: string
  ticketAssigneeId?: number | null
  ticketReasons?: string[] | null
  ticketStatuses?: string[] | null
  ticketTypes?: string[] | null
}

export interface TicketListRow extends Ticket {
  _isChildRow?: boolean
  allowAsParent: boolean
  assignee?: {
    fullName: string
  }
  childTickets: Array<{
    assignee?: { fullName: string; id: number }
    contact?: { fullName: string; id: number }
    createdAt: string
    home: null | { property: { isAutopilot: null | boolean; landingDirect: null | boolean } }
    id: number
    internalTicketStatus: string
    lastCommentDatetime: string | null
    marketSegment?: { name: string }
    priority: Priority
    reservation: null | { professionalHousing: null | boolean; standby: null | boolean }
    subject: string
    ticketReason: string
    ticketType: string
  }>
  contact?: {
    fullName: string
  }
  home: null | {
    property: {
      isAutopilot: null | boolean
      landingDirect: null | boolean
    }
  }
  lastCommentDatetime: string | null
  marketSegment?: {
    name: string
  }
  parentTicket: { id: number; subject: string; ticketType?: string } | null
  parentTicketId: number | null
  reservation: null | {
    professionalHousing: null | boolean
    standby: null | boolean
  }
}

export interface NewTicketDetails extends TicketListRow {
  description?: string
  homeId?: number
  permissionToEnter: boolean
  reservationId?: number
}
